'use client';

import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import type { LoadingContent } from '@/shared/lib/loading-content';
import type { LoadingFactItem } from './use-loading-facts';
import { loadingFactsService } from '../api/loading-facts.service';
import { seenFactsBatcher } from '../api/seen-facts-batcher';
import {
  getLoadingContentTracker,
  getUserStats,
} from '@/shared/lib/loading-content-tracker';

export interface UseLoadingContentOptions {
  isActive: boolean;
  rotationInterval?: number;
  trackRender?: boolean;
  contextualFacts?: LoadingFactItem[];
  seenIds?: string[];
  /** Project id — required to request regeneration when the pool is exhausted. */
  projectId?: string;
}

export interface LoadingContentState {
  currentContent: LoadingContent | null;
  userStats: {
    renderCount: number;
    sessionDuration: number;
  };
  markContentSeen: (contentId: string) => void;
  getNextContent: () => void;
}

const DEFAULT_ROTATION_INTERVAL = 25000; // 25 seconds
/** Min on-screen time before a fact counts as "viewed" and is recorded server-side. */
const VIEWED_THRESHOLD_MS = 8000;

function requestFactsRegeneration(projectId: string): void {
  loadingFactsService.regenerate(projectId).catch(() => {
    // Silently ignore — server is async, WS will deliver new facts
  });
}

export function useLoadingContent({
  isActive,
  rotationInterval = DEFAULT_ROTATION_INTERVAL,
  trackRender = false,
  contextualFacts,
  seenIds,
  projectId,
}: UseLoadingContentOptions): LoadingContentState {
  const [currentContent, setCurrentContent] = useState<LoadingContent | null>(
    null
  );
  const contentRotationRef = useRef<NodeJS.Timeout | null>(null);
  const tracker = getLoadingContentTracker();
  const hasTrackedRender = useRef(false);

  // Merge server-side seen IDs with local tracker on mount
  const serverSeenIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (seenIds && seenIds.length > 0) {
      serverSeenIds.current = new Set(seenIds);
      // Also mark in local tracker for immediate use
      for (const id of seenIds) {
        tracker.markContentSeen(id);
      }
    }
  }, [seenIds, tracker]);

  // Build contextual content from API facts, using API-provided IDs
  const contextualContent = useMemo((): LoadingContent[] => {
    if (!contextualFacts || contextualFacts.length === 0) return [];
    return contextualFacts.map((item) => ({
      id: item.id,
      type: item.type as LoadingContent['type'],
      text: item.text,
    }));
  }, [contextualFacts]);

  // Get random unseen content. Returns null when the pool is exhausted —
  // we no longer recycle; instead the caller asks the server for fresh facts.
  const getRandomContent = useCallback(
    (excludeIds: string[] = []): LoadingContent | null => {
      if (contextualContent.length === 0) return null;
      const availableContent = contextualContent.filter(
        (content) => !excludeIds.includes(content.id)
      );
      if (availableContent.length === 0) return null;
      return availableContent[
        Math.floor(Math.random() * availableContent.length)
      ] ?? null;
    },
    [contextualContent]
  );

  // Initialize content
  useEffect(() => {
    if (!currentContent && contextualContent.length > 0) {
      const seenContentIds = tracker.getSeenContentIds();
      const content = getRandomContent(seenContentIds);
      if (content) {
        setCurrentContent(content);
      } else if (projectId) {
        requestFactsRegeneration(projectId);
      }
    }
  }, [tracker, currentContent, contextualContent, getRandomContent, projectId]);

  // Mark current fact as "viewed" (and persist server-side) only after it has
  // been on screen ≥8s. Cleared by the next currentContent change / unmount.
  useEffect(() => {
    if (!currentContent) return;
    const id = currentContent.id;
    const timer = setTimeout(() => {
      tracker.markContentSeen(id);
      seenFactsBatcher.enqueue(id);
    }, VIEWED_THRESHOLD_MS);
    return () => clearTimeout(timer);
  }, [currentContent, tracker]);

  // Track render count once
  useEffect(() => {
    if (trackRender && isActive && !hasTrackedRender.current) {
      tracker.incrementRenderCount();
      hasTrackedRender.current = true;
    }
  }, [trackRender, isActive, tracker]);

  // Keep a live ref to the latest content picker so the rotation interval reads
  // the freshest pool without being torn down on every WS batch. Re-creating the
  // interval whenever facts updated reset the 25s countdown, so a quote could
  // linger far longer than intended (the symmetric mobile bug flipped quotes
  // every 2-3s). The interval now owns the cadence independently of pool updates.
  const getRandomContentRef = useRef(getRandomContent);
  useEffect(() => {
    getRandomContentRef.current = getRandomContent;
  }, [getRandomContent]);

  // Rotate content periodically
  useEffect(() => {
    if (!isActive) {
      if (contentRotationRef.current) {
        clearInterval(contentRotationRef.current);
        contentRotationRef.current = null;
      }
      return;
    }

    const rotateContent = () => {
      if (!isActive) return;
      const seenContentIds = tracker.getSeenContentIds();
      const newContent = getRandomContentRef.current(seenContentIds);
      if (newContent) {
        setCurrentContent(newContent);
      } else if (projectId) {
        // Pool exhausted on this user × project — ask server for a fresh
        // batch (async, arrives via WS). Hide content until then; we never
        // recycle a fact the user has already seen ≥8s on any device.
        requestFactsRegeneration(projectId);
        setCurrentContent(null);
      }
    };

    contentRotationRef.current = setInterval(rotateContent, rotationInterval);

    return () => {
      if (contentRotationRef.current) {
        clearInterval(contentRotationRef.current);
        contentRotationRef.current = null;
      }
    };
  }, [isActive, tracker, rotationInterval, projectId]);

  // Flush pending seen IDs on unmount
  useEffect(() => {
    return () => {
      seenFactsBatcher.flushNow();
    };
  }, []);

  // Manual content selection. Setting currentContent restarts the 8s
  // viewed-threshold timer in the effect above; we don't mark seen here.
  const getNextContent = useCallback(() => {
    const seenContentIds = tracker.getSeenContentIds();
    const newContent = getRandomContent(seenContentIds);
    if (newContent) {
      setCurrentContent(newContent);
    } else if (projectId) {
      requestFactsRegeneration(projectId);
      setCurrentContent(null);
    }
  }, [tracker, getRandomContent, projectId]);

  // Mark content as seen
  const markContentSeen = useCallback(
    (contentId: string) => {
      tracker.markContentSeen(contentId);
      seenFactsBatcher.enqueue(contentId);
    },
    [tracker]
  );

  return {
    currentContent,
    userStats: getUserStats(),
    markContentSeen,
    getNextContent,
  };
}

/**
 * Hook to get localized content type labels from render.json translations.
 */
export function useLoadingLabels() {
  const t = useTranslations('render.loadingLabels');
  return useMemo(
    () => ({
      didYouKnow: t('didYouKnow'),
      designTip: t('designTip'),
      buildingTrivia: t('buildingTrivia'),
    }),
    [t]
  );
}
